import { Alert, FormControlLabel, Switch } from '@mui/material';

import { colors } from 'src/theme';

interface ChangedUnderneathBannerProps {
  count: number;
  active: boolean;
  onToggle: (active: boolean) => void;
}

/**
 * "Values moved underneath you while you were resolving."
 *
 * Renders nothing at a count of zero, which is the normal case — nobody else
 * saved during your session. It appears when a submit is rejected because the
 * current version moved, so the message is an instruction to re-resolve rather
 * than a neutral statement of fact, and it carries the conflict colour because
 * it is reporting new conflicts.
 */
const ChangedUnderneathBanner = ({
  count,
  active,
  onToggle,
}: ChangedUnderneathBannerProps) => {
  if (count <= 0) {
    return null;
  }

  return (
    <Alert
      severity="error"
      sx={{
        mb: 1,
        py: 0,
        // The conflict tone, not MUI's own error palette: this banner reports
        // conflicts, so it reads as the same thing the red cells do.
        backgroundColor: colors.conflict.red.bg,
        color: colors.conflict.red.fg,
        '& .MuiAlert-icon': { color: colors.conflict.red.fg },
      }}
      action={
        <FormControlLabel
          // One line, and clear of the right edge.
          sx={{ m: 0, mr: 3, whiteSpace: 'nowrap', flexShrink: 0 }}
          control={
            <Switch
              size="small"
              checked={active}
              onChange={(_e, checked) => onToggle(checked)}
            />
          }
          label="Show only these"
          componentsProps={{ typography: { noWrap: true } }}
        />
      }
    >
      {count === 1
        ? '1 Value has changed and needs resolution, please resolve this conflict.'
        : `${count} Values have changed and need resolution, please resolve this conflict.`}
    </Alert>
  );
};

export default ChangedUnderneathBanner;
